import styles from './ProjectHourBillingPreview.module.scss';

import { ChevronLeft, ChevronRight, Info } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Loading } from '@work4all/components';
import { Tooltip } from '@work4all/components/lib/components/tooltip/Tooltip';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';

import { BillProjectHoursResult } from '@work4all/models/lib/Classes/BillProjectHoursResult.entity';
import { ValidationMessage } from '@work4all/models/lib/Classes/ValidationMessage.entity';

import { useFormContextPlus } from '../../../../../containers/mask-overlays/form-plus/use-form-context-plus';
import { SimplePDFReport } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/simple-pdf-report/SimplePDFReport';
import { bzObjToEntityType } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/simple-pdf-report/utils';
import { useBillProjectHoursResult } from '../hooks/use-bill-project-hours-result';
import { useInputsValid } from '../hooks/use-inputs-valid';
import { ProjectHourBillingOverlayFormValue } from '../types';

export const ProjectHourBillingPreview = () => {
  const [generatedData, setGeneratedData] =
    useState<BillProjectHoursResult>(null);
  const [loading, setLoading] = useState(false);

  const form = useFormContextPlus<ProjectHourBillingOverlayFormValue>();

  const { watch, setValue } = form;
  const { mode, filters, settings, previewIndex } = watch();

  const { generatePreview } = useBillProjectHoursResult({
    entity: bzObjToEntityType[settings.bzObjType],
  });

  const [warnings, setWarnings] = useState<ValidationMessage[]>([]);

  useEffect(() => {
    setLoading(true);
    setWarnings([]);
    generatePreview({
      variables: {
        filters: {
          ...filters,
          projectTimeCodes:
            mode === 'projectTimes' ? filters.projectTimeCodes : undefined,
        },
        settings: {
          ...settings,
          documentDate: DateTime.fromISO(settings.documentDate).toFormat(
            'yyyy-MM-dd'
          ),
          dryRun: true,
          dryRunSkip: previewIndex - 1,
        },
      },
      onCompleted: (data) => {
        if (previewIndex > data.billProjectHours.totalBzObjectCount) {
          setValue('previewIndex', 1);
          return;
        }
        setWarnings(data.billProjectHours?.warnings || []);
        setLoading(false);
        setGeneratedData(data.billProjectHours);
      },
    });
  }, [settings, filters, generatePreview, mode, previewIndex, setValue]);

  const { t } = useTranslation();

  const handlePageClick = useCallback(
    (value: number) => {
      let newValue = previewIndex + value;

      if (newValue > generatedData?.totalBzObjectCount) {
        newValue = 1;
      }
      if (newValue < 1) {
        newValue = generatedData?.totalBzObjectCount;
      }
      setValue('previewIndex', newValue);
    },
    [generatedData?.totalBzObjectCount, previewIndex, setValue]
  );

  const inputsValid = useInputsValid(form);

  const count = generatedData?.totalBzObjectCount || 0;

  const previewText = loading
    ? t('COMMON.GENERATING_BILLS_COUNT_LOADING')
    : t('COMMON.GENERATING_BILLS_COUNT', { count });

  return (
    <Stack height="100%">
      <Stack alignItems="center" p="1rem" gap="1rem">
        {!inputsValid ? (
          <Stack className={clsx(styles.alertBox, styles.warning)} width="100%">
            <Typography variant="h4">
              {t('COMMON.PROJECT_BILLING.NO_DATA.TITLE')}:
            </Typography>
            <Typography>
              {t('COMMON.PROJECT_BILLING.NO_DATA.MESSAGE')}
            </Typography>
          </Stack>
        ) : (
          <Stack className={styles.alertBox} width="100%">
            <Tooltip title={t('COMMON.PROJECT_BILLING.PREVIEW.TOOLTIP')}>
              <Stack
                direction="row"
                flex={1}
                gap="0.25rem"
                justifyContent="center"
              >
                <Info />
                <Typography variant="h4">{t('COMMON.PREVIEW')}:</Typography>
                <Typography>{previewText}</Typography>
              </Stack>
            </Tooltip>
            {generatedData?.totalBzObjectCount > 1 && (
              <Stack direction="row" alignItems="center">
                <IconButton onClick={() => handlePageClick(-1)}>
                  <ChevronLeft />
                </IconButton>
                <Typography color="var(--text03)">
                  {previewIndex}/{generatedData?.totalBzObjectCount}
                </Typography>
                <IconButton onClick={() => handlePageClick(1)}>
                  <ChevronRight />
                </IconButton>
              </Stack>
            )}
          </Stack>
        )}

        {warnings.length > 0 &&
          warnings.map((warning) => (
            <Stack
              className={clsx(styles.alertBox, styles.warning)}
              width="100%"
            >
              <Typography variant="h4">
                {t('COMMON.PROJECT_BILLING.' + warning.property)}
              </Typography>
            </Stack>
          ))}
      </Stack>

      <Divider style={{ padding: 0 }} />
      {!inputsValid ? null : loading ? (
        <Loading />
      ) : (
        <Stack className={styles.pdfPreview}>
          {generatedData?.dryRunBzObject && (
            <SimplePDFReport
              data={generatedData.dryRunBzObject}
              bzObjType={settings.bzObjType}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};
